import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * MiniScanLiveQuestions — asteptarea mini-scanului, umpluta cu continut real.
 *
 * Pana acum ecranul de asteptare era o singura linie de text dintr-un ticker de
 * 4 mesaje care se roteau la 4200ms. Un ciclu complet dura 16,8s, iar scanul
 * tipic 15-30s: userul vedea „Extrag verdictul…" urmat de „Clasific brandul…",
 * adica bara se intorcea la inceput in fata lui. Un progres care regreseaza nu
 * ascunde asteptarea — anunta ca e fals.
 *
 * Ce arata acum, in ordinea in care apare:
 *   1. industria in care a fost incadrat brandul, cu provenienta ei (pasul 1,
 *      POST /mini-scan/classify — instant pentru brandurile pe care le stim);
 *   2. exact cele 9 intrebari pe care le trimitem lui ChatGPT, una cate una, cu
 *      timp de citit (pasul 2, POST /mini-scan/prepare). Sunt string-urile
 *      trimise efectiv la model — nu o repovestire.
 *
 * CE E REAL SI CE NU, explicit, ca sa nu se piarda la urmatoarea atingere:
 *  - industria si sursa ei: REALE — registru / scanare anterioara / `brands` /
 *    incadrare facuta de model, spuse ca atare, fara sa fie topite intr-una;
 *  - textele intrebarilor: REALE, verbatim din `build_consumer_prompts`;
 *  - faptul ca sunt puse chiar acum: REAL — scanul ruleaza in paralel cu
 *    dezvaluirea, cele 9 conversatii pleaca simultan (asyncio.gather);
 *  - RITMUL dezvaluirii: SINGURUL element de prezentare. De aceea copy-ul nu
 *    spune niciodata „acum o trimit pe asta" — ar fi fals, toate pleaca odata.
 *    Cu `prefers-reduced-motion` dezvaluirea dispare si apar toate deodata.
 *  - contorul: numara intrebari AFISATE, nu raspunsuri primite. Raspunsurile nu
 *    sunt vizibile fara streaming, deci nu exista niciun contor care sa le
 *    pretinda.
 *
 * BARA DE PROGRES (2026-08-19): exista, si e ancorata pe evenimentele reale ale
 * celor trei pasi — nu pe un cronometru care umple singur. Nu regreseaza si nu
 * ajunge la 100% inainte de verdict (detaliile la PHASE_PLAN, mai jos). NU
 * afiseaza niciun procent si niciun ETA: fereastra e masurata statistic, dar
 * scanul in curs nu raporteaza nimic intre plecarea celor 9 conversatii si
 * verdict, deci un numar langa bara ar fi o estimare data drept masuratoare.
 * Cronometrul ramane o numaratoare — singura cifra pe care o stim exact.
 */

const GOLD = 'var(--gold, #D4AF37)';
const MONO = 'DM Mono, monospace';
const BODY = 'Cormorant Garamond, serif';

// Provenienta industriei, cu cheile scrise LITERAL (nu prin template literal):
// guard-ul din i18n/__tests__/mini_scan_live_keys.test.js nu poate vedea chei
// construite dinamic, iar o cheia stearsa ar trimite string-ul brut pe ecran.
// Fiecare sursa isi spune propriul adevar — „din catalogul nostru" si „asa
// te-a incadrat ChatGPT" nu sunt acelasi lucru si nu au voie sa fie topite
// intr-o formulare comoda.
// Cheile pasilor, tot LITERAL, din acelasi motiv: `t(`...${s.key}`)` nu poate
// fi vazut de guard-ul de chei, iar un pas ramas fara traducere ar scrie
// „app.mini_scan.v2.live.step_..." pe homepage, sub bara de progres.
const STEP_KEY = {
  industry: 'app.mini_scan.v2.live.step_industry',
  questions: 'app.mini_scan.v2.live.step_questions',
  verdict: 'app.mini_scan.v2.live.step_verdict',
};

const INDUSTRY_SOURCE_KEY = {
  registry: 'app.mini_scan.v2.live.industry_source_registry',
  registry_partial: 'app.mini_scan.v2.live.industry_source_registry',
  brands_db: 'app.mini_scan.v2.live.industry_source_brands_db',
  llm: 'app.mini_scan.v2.live.industry_source_llm',
};

// Ritmul de dezvaluire, calibrat pe FEREASTRA REALA de asteptare, masurata pe
// 178 de scanuri complete din productie (mini_scan_calls, 30 de zile):
//   clasificare p50 1,3s · cea mai lenta din 9 conversatii p50 7,1s ·
//   judecator p50 9,5s  →  TOTAL p50 18,3s, p90 24,4s, max 32,5s.
// Clasificarea se intampla inaintea scanului (pasii 1 si 2), deci intrebarile
// apar la ~1,3s (brand cunoscut: doar profilul) sau ~2,5s (brand necunoscut:
// identificare + profil), si mai raman ~16-17 secunde de umplut pana la
// verdict. Masuratoarea se reface din `mini_scan_calls` dupa ce cele doua noi
// tipuri de apel (`identify`, `profile`) au istoric propriu.
//
// De ce nu ritmul de CITIT integral (~3,9s/intrebare, adica 35s): ar depasi
// asteptarea de doua ori, iar raportul ar taia transcriptul pe la a cincea
// intrebare. Bulele RAMAN pe ecran si sunt derulabile, deci pasul nu trebuie sa
// acopere cititul complet — doar sa lase fiecare aparitie sa fie observata.
// ~1,8s/intrebare → 15,9s pentru toate 9, fix in fereastra masurata.
//
// Daca latenta scade sub ~16s, ultimele intrebari sunt taiate de raport. E
// alegerea corecta: rezultatul e ce a venit userul sa ia.
const READ_MS_PER_CHAR = 15;
const READ_BASE_MS = 900;
const READ_MAX_MS = 2600;

function readingTime(text) {
  return Math.min(READ_MAX_MS, READ_BASE_MS + (text || '').length * READ_MS_PER_CHAR);
}

// ── Bara de progres ──────────────────────────────────────────────────────
// Ancorata pe EVENIMENTE REALE — industria stabilita (pasul 1), intrebarile
// primite (pasul 2), scanul plecat (pasul 3) — cu o crestere intre ele
// calculata din aceeasi fereastra masurata pe 178 de scanuri din productie.
//
// Trei reguli, in ordinea importantei:
//  1. NU REGRESEAZA. Un progres care se intoarce nu ascunde asteptarea, o
//     denunta ca falsa — exact asa a murit tickerul de 4 mesaje care se bucla
//     la 16,8s, sub durata scanului.
//  2. NU AJUNGE LA 100% inainte de verdict. Cresterea in interiorul unei faze
//     e ASIMPTOTICA: se apropie de plafonul fazei fara sa-l atinga niciodata,
//     deci si la 32,5s (cel mai lent scan masurat) bara inca se misca, fara sa
//     fi promis vreodata ca s-a terminat. Singurul lucru care o termina e
//     rezultatul, iar atunci ecranul asta dispare cu totul.
//  3. SALTURILE INAINTE sunt stiri reale, nu decor: bara sare cand industria e
//     stabilita si cand intrebarile au sosit. Restul e curgere.
//
// PROCENTUL NU SE SCRIE pe ecran. Geometria barei e o estimare calibrata pe o
// distributie; un numar langa ea ar invita la aritmetica pe o marime pe care
// n-o masuram in timp real (scanul nu are streaming — nu exista niciun semnal
// de la server intre plecarea celor 9 conversatii si verdict). Bara comunica
// „lucreaza acum"; cronometrul de langa ea e singura cifra exacta.
const PHASE_PLAN = {
  classifying: { from: 2, to: 15, tau: 1300 },   // clasificare p50 1,3s
  preparing: { from: 15, to: 35, tau: 2600 },    // concurenti + cele 9 intrebari
  scanning: { from: 35, to: 96, tau: 9000 },     // 9 conversatii + judecator, p50 ~16s
};

const TICK_MS = 100;

/**
 * Pozitia barei, ca functie pura — testabila fara sa randezi nimic.
 *
 * `floor` e valoarea deja atinsa: garanteaza monotonia si peste schimbarea de
 * faza (regula 1). Curba `to - (to-from)*e^(-t/tau)` nu atinge `to` pentru
 * niciun t finit, deci plafonul fazei ramane o limita, nu o destinatie.
 */
export function progressAt(phase, phaseElapsedMs, floor = 0) {
  const plan = PHASE_PLAN[phase] || PHASE_PLAN.scanning;
  const t = Math.max(0, phaseElapsedMs || 0);
  const eased = plan.to - (plan.to - plan.from) * Math.exp(-t / plan.tau);
  return Math.max(floor, Math.min(plan.to, eased));
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Props:
 *  - questions: [{index, text, redacted, hidden_reason, skip_reason}] de la
 *    /mini-scan/prepare. Gol cat timp pregatirea inca ruleaza.
 *  - categorie: industria stabilita la pasul 1 (/mini-scan/classify)
 *  - industrySource: de unde vine ea — 'registry' | 'registry_partial' |
 *    'brands_db' | 'llm'. Decide eticheta de provenienta.
 *  - phase: 'classifying' | 'preparing' | 'scanning' — faza REALA, nu una simulata
 *  - onRevealDone: se apeleaza cand toate intrebarile au fost afisate
 */
export default function MiniScanLiveQuestions({
  questions = [],
  categorie = null,
  industrySource = null,
  phase = 'preparing',
  onRevealDone = null,
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState(PHASE_PLAN.classifying.from);
  const startRef = useRef(null);
  const listRef = useRef(null);
  const doneRef = useRef(false);
  // Bara se calculeaza in tick-ul cronometrului, care ruleaza intr-un effect
  // montat o singura data. De aceea faza si valoarea atinsa traiesc in refs:
  // closure-ul acelui effect nu vede prop-urile ultimului render.
  const phaseRef = useRef(phase);
  const phaseStartRef = useRef(null);
  const progressRef = useRef(PHASE_PLAN.classifying.from);

  // Intrebarile care au ceva de aratat. Un slot sarit ramane in numitor (9),
  // dar nu produce o bula goala pe ecran.
  const visible = useMemo(
    () => questions.filter(q => q && (q.text || q.skip_reason || q.hidden_reason)),
    [questions],
  );

  // ── Faza s-a schimbat = veste reala: bara sare inainte si reporneste ──
  // ceasul fazei. `progressRef` ramane podeaua, deci saltul e mereu in fata.
  useEffect(() => {
    phaseRef.current = phase;
    phaseStartRef.current = Date.now();
    const floor = Math.max(progressRef.current, (PHASE_PLAN[phase] || {}).from || 0);
    progressRef.current = floor;
    setProgress(floor);
  }, [phase]);

  // ── Cronometrul si bara: o numaratoare reala, pornita la primul mount ──
  useEffect(() => {
    if (startRef.current === null) startRef.current = Date.now();
    if (phaseStartRef.current === null) phaseStartRef.current = startRef.current;
    const id = setInterval(() => {
      const now = Date.now();
      setElapsed(now - startRef.current);
      const next = progressAt(phaseRef.current, now - phaseStartRef.current, progressRef.current);
      progressRef.current = next;
      setProgress(next);
    }, TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Lista noua (alt brand) → transcriptul o ia de la capat.
  useEffect(() => {
    setShown(0);
    doneRef.current = false;
  }, [visible]);

  // ── Dezvaluirea, cu timp de citit per intrebare ──────────────────────
  // Un singur timeout la un moment dat, replanificat dupa lungimea intrebarii
  // DEJA afisate: cat sta ea pe ecran inainte sa apara urmatoarea. Un interval
  // fix ar da acelasi ragaz unei intrebari de 4 cuvinte si uneia de 20.
  useEffect(() => {
    if (visible.length === 0) return undefined;
    if (prefersReducedMotion()) { setShown(visible.length); return undefined; }
    if (shown >= visible.length) return undefined;

    const delay = shown === 0 ? 250 : readingTime(visible[shown - 1]?.text);
    const id = setTimeout(() => setShown(s => s + 1), delay);
    return () => clearTimeout(id);
  }, [visible, shown]);

  useEffect(() => {
    if (visible.length > 0 && shown >= visible.length && !doneRef.current) {
      doneRef.current = true;
      if (onRevealDone) onRevealDone();
    }
  }, [shown, visible.length, onRevealDone]);

  // Bula noua → o aducem in vedere, dar niciodata smucind pagina intreaga:
  // widgetul poate fi sub fold pe mobil, iar un `scrollIntoView` pe container
  // ar muta viewportul sub degetul userului.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [shown]);

  const seconds = (elapsed / 1000).toFixed(1);
  const revealing = shown < visible.length;

  // Textul fazei se calculeaza O SINGURA DATA: il citesc si antetul, si
  // `aria-valuetext` al barei. Un screen reader aude astfel starea reala
  // („pregatesc intrebarile"), nu un procent pe care nu-l masuram.
  //
  // Fara intrebari pe ecran, faza spune EXACT unde suntem. Al treilea caz nu e
  // teoretic: daca `prepare` pica, ori daca brandul nu e recunoscut deloc,
  // scanul ruleaza cu transcriptul gol — iar „pregatesc intrebarile" ar fi
  // atunci fals.
  //
  // Conditia e pe `shown`, nu pe `visible.length`: intre momentul in care
  // intrebarile SOSESC si momentul in care apare prima bula trec ~250ms, iar
  // pe `visible.length` antetul scria in fereastra aia „Intrebarea 0 din 9".
  const phaseText = shown === 0
    ? t(phase === 'classifying'
      ? 'app.mini_scan.v2.live.phase_classifying'
      : phase === 'scanning'
        ? 'app.mini_scan.v2.live.phase_scanning_blind'
        : 'app.mini_scan.v2.live.phase_preparing')
    : revealing
      ? t('app.mini_scan.v2.live.phase_asking', { n: shown, total: questions.length || 9 })
      : t('app.mini_scan.v2.live.phase_reading');

  // Cei trei pasi, fiecare legat de o STARE REALA, nu de un cronometru:
  //  1. industria — bifata cand `classify` (sau `prepare`) a intors-o;
  //  2. intrebarile — bifate cand au sosit efectiv de la `prepare`;
  //  3. verdictul — activ cat ruleaza scanul si NICIODATA bifat aici: cand
  //     verdictul exista, widgetul inlocuieste ecranul asta cu rezultatul.
  const steps = [
    { key: 'industry', labelKey: STEP_KEY.industry, done: Boolean(categorie), active: phase === 'classifying' },
    { key: 'questions', labelKey: STEP_KEY.questions, done: visible.length > 0, active: phase === 'preparing' },
    { key: 'verdict', labelKey: STEP_KEY.verdict, done: false, active: phase === 'scanning' },
  ];

  return (
    <div style={{ marginTop: 18 }}>
      {/* ── Antet: faza reala + cronometru ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        gap: 10, flexWrap: 'wrap', marginBottom: 10,
      }}>
        <div aria-live="polite" style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted, #6B7280)', letterSpacing: 0.4 }}>
          {phaseText}
        </div>
        <div
          style={{ fontFamily: MONO, fontSize: 11, color: 'var(--muted, #6B7280)', fontVariantNumeric: 'tabular-nums' }}
          aria-hidden="true"
        >
          {seconds}s
        </div>
      </div>

      {/* ── Bara de progres: semnalul „lucreaza acum" ──────────────────── */}
      <ProgressTrack value={progress} valueText={phaseText} steps={steps} t={t} />

      {/* ── Propozitia de deschidere: industria + ce urmeaza ──────────────
          Primul lucru real pe care il vede userul, inaintea oricarei intrebari.
          Eticheta de provenienta de dedesubt nu e ornament: industria vine ori
          din datele noastre, ori dintr-o incadrare facuta de model, iar userul
          trebuie sa stie a cui afirmatie citeste. */}
      {categorie && (
        <div style={{
          padding: '10px 12px', borderRadius: 8, marginBottom: 10,
          background: 'rgba(212,175,55,0.08)',
        }}>
          <div style={{ fontFamily: BODY, fontSize: 16, color: 'var(--ink, #0A1428)', lineHeight: 1.45 }}>
            {t('app.mini_scan.v2.live.industry_line', { categorie })}
          </div>
          <div style={{
            fontFamily: MONO, fontSize: 10, color: 'var(--muted, #6B7280)',
            marginTop: 5, lineHeight: 1.5,
          }}>
            {t(INDUSTRY_SOURCE_KEY[industrySource] || INDUSTRY_SOURCE_KEY.llm)}
          </div>
        </div>
      )}

      {/* ── Transcriptul: intrebarile, ca bule de chat pe partea userului ── */}
      {/* aria-live="off": bulele apar la interval de cateva secunde si ar face
          screen reader-ul sa vorbeasca peste sine. Starea se anunta o singura
          data, prin randul de faza de mai sus. */}
      <div
        ref={listRef}
        aria-live="off"
        style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          maxHeight: 260, overflowY: 'auto',
          padding: '2px 2px 2px 0',
        }}
      >
        {visible.slice(0, shown).map(q => (
          <QuestionBubble key={q.index} q={q} t={t} />
        ))}

        {revealing && visible.length > 0 && <TypingDots />}
      </div>

      {/* ── Nota de subsol: ce se intampla acum, fara sa promita ce nu putem ── */}
      <div style={{
        fontFamily: MONO, fontSize: 10, color: 'var(--muted, #6B7280)',
        marginTop: 10, lineHeight: 1.6, letterSpacing: 0.3,
      }}>
        {phase === 'scanning'
          ? t('app.mini_scan.v2.live.footnote_scanning')
          : phase === 'classifying'
            ? t('app.mini_scan.v2.live.footnote_classifying')
            : t('app.mini_scan.v2.live.footnote_preparing')}
      </div>
    </div>
  );
}

/**
 * Bara de progres + cei trei pasi + randul care cere pagina deschisa.
 *
 * `role="progressbar"` cu `aria-valuetext` = textul fazei: un screen reader
 * aude „pregătesc întrebările", nu „35 la suta". `aria-valuenow` ramane pentru
 * tehnologiile care il cer, dar nu e niciodata VORBIT ca procent si nu apare
 * nicaieri pe ecran (vezi comentariul de la PHASE_PLAN).
 *
 * Latimea are `transition` pe 400ms liniar: tick-ul e la 100ms, iar fara
 * tranzitie bara ar avansa in trepte vizibile. Sclipirea care traverseaza
 * portiunea plina exista pentru cazul in care cresterea devine prea lenta ca sa
 * se vada — spre finalul cozii asimptotice, la 25-30s, bara castiga fractiuni
 * de procent pe secunda si ar parea inghetata exact cand userul e cel mai
 * aproape sa plece. `prefers-reduced-motion` o opreste (globals.css).
 */
function ProgressTrack({ value, valueText, steps, t }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(value)}
        aria-valuetext={valueText}
        aria-label={t('app.mini_scan.v2.live.progress_label')}
        style={{
          position: 'relative',
          height: 6,
          borderRadius: 999,
          background: 'rgba(10,20,40,0.07)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${value}%`,
            height: '100%',
            borderRadius: 999,
            background: `linear-gradient(90deg, rgba(212,175,55,0.55) 0%, ${GOLD} 100%)`,
            transition: 'width 400ms linear',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
              animation: 'pbProgressShine 1600ms linear infinite',
            }}
          />
        </div>
      </div>

      {/* Cei trei pasi: bifa apare pe stare reala, nu pe cronometru. */}
      <div style={{
        display: 'flex', gap: 14, flexWrap: 'wrap',
        marginTop: 8,
        fontFamily: MONO, fontSize: 10, letterSpacing: 0.4,
      }}>
        {steps.map(s => (
          <span
            key={s.key}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              color: s.done || s.active ? 'var(--ink, #0A1428)' : 'var(--muted, #6B7280)',
              opacity: s.done || s.active ? 1 : 0.55,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 7, height: 7, borderRadius: '50%',
                background: s.done ? GOLD : 'transparent',
                border: `1.5px solid ${s.done || s.active ? GOLD : 'rgba(10,20,40,0.25)'}`,
                animation: s.active && !s.done ? 'pbTypingDot 1400ms infinite ease-in-out' : 'none',
              }}
            />
            {t(s.labelKey)}
          </span>
        ))}
      </div>

      {/* Motivul pentru care randul asta exista: mini_scan_abandoned arata
          oameni care pleaca in timpul asteptarii. Nu promite un timp anume —
          „de obicei sub 30 de secunde" e chiar masuratoarea (p90 24,4s, max
          32,5s pe 178 de scanuri), nu o cifra de marketing. */}
      <div style={{
        fontFamily: MONO, fontSize: 10, color: 'var(--muted, #6B7280)',
        marginTop: 8, lineHeight: 1.6, letterSpacing: 0.3,
      }}>
        {t('app.mini_scan.v2.live.hold_on')}
      </div>
    </div>
  );
}

/** O intrebare, in forma in care a plecat la model. */
function QuestionBubble({ q, t }) {
  const skipped = !q.text;

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{
        maxWidth: '94%',
        background: skipped ? 'rgba(0,0,0,0.035)' : 'rgba(212,175,55,0.14)',
        border: skipped ? '1px dashed rgba(0,0,0,0.12)' : '1px solid rgba(212,175,55,0.35)',
        borderRadius: '14px 14px 4px 14px',
        padding: '9px 13px',
        animation: 'pbQuestionIn 260ms ease-out both',
      }}>
        <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--muted, #6B7280)', letterSpacing: 1.4, marginBottom: 4 }}>
          {t('app.mini_scan.v2.live.question_label', { n: q.index })}
        </div>

        {skipped ? (
          <div style={{ fontFamily: MONO, fontSize: 11.5, color: 'var(--muted, #6B7280)', lineHeight: 1.5 }}>
            {q.hidden_reason
              ? t('app.mini_scan.v2.live.question_hidden')
              : t('app.mini_scan.v2.live.question_skipped')}
          </div>
        ) : (
          <div style={{ fontFamily: BODY, fontSize: 16, color: 'var(--ink, #0A1428)', lineHeight: 1.45 }}>
            {q.text}
          </div>
        )}

        {/* Concurentul din duel vine din lista pe care modelul o DECLARA la
            clasificare, nemasurata si nefiltrata contra brandurilor scoase din
            stratul public. Se redacteaza server-side; aici doar explicam de ce
            userul vede un bloc in loc de un nume. */}
        {q.redacted && (
          <div style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--muted, #6B7280)', marginTop: 5, lineHeight: 1.5 }}>
            {t('app.mini_scan.v2.live.question_redacted')}
          </div>
        )}
      </div>
    </div>
  );
}

/** Indicatorul de „mai vine una" — familiar din orice UI de chat. */
function TypingDots() {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }} aria-hidden="true">
      <div style={{
        background: 'rgba(0,0,0,0.04)', borderRadius: '14px 14px 4px 14px',
        padding: '11px 14px', display: 'flex', gap: 4,
      }}>
        {[0, 1, 2].map(i => (
          <span
            key={i}
            style={{
              width: 5, height: 5, borderRadius: '50%', background: GOLD,
              animation: `pbTypingDot 1200ms ${i * 160}ms infinite ease-in-out`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
